import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Chip,
  Modal,
  Container,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Alert,
  InputAdornment,
  Stack,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Visibility,
  Search,
  Edit,
  ContentCopy,
  ArrowBack,
  Add,
  PersonOutlined,
  Delete,
  PhotoCamera,
  Description,
  MoreVert,
} from "@mui/icons-material";
import { Form, useNavigate } from "react-router-dom";
import api from "../api";
import { uiPT, statusTextPT } from "../i18n";
import { useAuth } from "../hooks/useAuth";
import { getStatusColor, copyToClipboard } from "../utils";
import Card from "../components/Card";

interface Client {
  _id: string;
  trackingCode: string;
  name: string;
  email: string;
  type: string;
  modality: string;
  phoneNumber: string;
  IBAN: string;
  identityType: string;
  city: string;
  status: string;
  rejectionReason?: string;
  verificationLink?: string;
  profilePhotoUploadLink?: string;
  idDocumentLink?: string;
  rejectedDocuments?: {
    selfie?: boolean;
    modalityPhoto?: boolean;
    driverLicense?: boolean;
    idCardFront?: boolean;
    idCardBack?: boolean;
    idCardPdf?: boolean;
    caeDocument?: boolean;
    taxClearanceDocument?: boolean;
  };
  idCard: {
    front?: string;
    back?: string;
    pdf?: string;
  };
  selfie: string;
  modalityPhoto: string;
  driverLicense?: string;
  createdAt: string;
}

export default function DashboardPage() {
  const { user } = useAuth();

  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [filePreview, setFilePreview] = useState<{
    url: string;
    type: string;
    fallbackUrl?: string;
  } | null>(null);
  const [statusUpdateDialog, setStatusUpdateDialog] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [verificationLink, setVerificationLink] = useState("");
  const [rejectedDocuments, setRejectedDocuments] = useState<{
    selfie?: boolean;
    modalityPhoto?: boolean;
    driverLicense?: boolean;
    idCardFront?: boolean;
    idCardBack?: boolean;
    idCardPdf?: boolean;
    caeDocument?: boolean;
    taxClearanceDocument?: boolean;
  }>({});
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletionReason, setDeletionReason] = useState("");
  const [linkDialog, setLinkDialog] = useState(false);
  const [linkType, setLinkType] = useState<"profilePhoto" | "idDocument">(
    "profilePhoto"
  );
  const [linkValue, setLinkValue] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedClientForMenu, setSelectedClientForMenu] =
    useState<Client | null>(null);

  const navigate = useNavigate();

  // Reset page to 1 when search or status filter changes
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  useEffect(() => {
    fetchClients();
  }, [page, search, statusFilter, user]);

  const fetchClients = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "10",
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
      });

      // Use unified clients endpoint for both admin and employee
      const endpoint = "/clients";
      console.log(
        "Fetching clients from endpoint:",
        endpoint,
        "with params:",
        params
      );

      const response = await api.get(endpoint, { params });
      console.log("Clients response:", response.data);

      setClients(response.data.data);
      setTotalPages(response.data.pagination.pages);
    } catch (error) {
      console.error("Error fetching clients:", error);
      console.error("Error details:", {
        message: (error as any).message,
        response: (error as any).response?.data,
        status: (error as any).response?.status,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!user || !selectedClient || !newStatus) return;

    try {
      const updateData: any = { status: newStatus };

      if (
        [
          "REJEITADO PELA EQUIPE",
          "DOCUMENTOS REJEITADOS",
          "DOCUMENTOS EXPIRADOS",
        ].includes(newStatus) &&
        rejectionReason
      ) {
        updateData.rejectionReason = rejectionReason;
      }

      if (
        (newStatus === "VERIFICAÇÃO ENVIADA" ||
          newStatus === "VERIFICAÇÃO REJEITADA") &&
        verificationLink
      ) {
        updateData.verificationLink = verificationLink;
      }

      // Handle document rejection statuses
      if (
        [
          "REJEITADO PELA EQUIPE",
          "DOCUMENTOS REJEITADOS",
          "DOCUMENTOS EXPIRADOS",
        ].includes(newStatus)
      ) {
        updateData.rejectedDocuments = rejectedDocuments;
      }

      const endpoint = "/clients";

      await api.put(`${endpoint}/${selectedClient._id}/status`, updateData);
      setStatusUpdateDialog(false);
      setNewStatus("");
      setRejectionReason("");
      setVerificationLink("");
      setRejectedDocuments({});
      setSelectedClient(null);
      fetchClients();
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleDeleteClient = async () => {
    if (!user || !clientToDelete || !deletionReason.trim()) return;

    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await api.delete(`/clients/${clientToDelete._id}`, {
        data: { deletionReason: deletionReason.trim() },
      });
      setDeleteDialog(false);
      setDeletionReason("");
      setDeleteSuccess(`Cliente ${clientToDelete.name} deletado com sucesso!`);
      setClientToDelete(null);
      fetchClients();
    } catch (error: any) {
      console.error("Error deleting client:", error);
      setDeleteError(
        error.response?.data?.message || "Erro ao deletar cliente"
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const openDeleteDialog = (client: Client) => {
    setClientToDelete(client);
    setDeleteDialog(true);
    setDeleteError(null);
    setDeleteSuccess(null);
    setDeletionReason("");
  };

  const handleLinkUpdate = async () => {
    if (!selectedClient || !linkValue.trim()) return;

    try {
      const endpoint =
        linkType === "profilePhoto"
          ? `/clients/${selectedClient._id}/profile-photo-upload-link`
          : `/clients/${selectedClient._id}/id-document-link`;

      const payload =
        linkType === "profilePhoto"
          ? { profilePhotoUploadLink: linkValue.trim() }
          : { idDocumentLink: linkValue.trim() };

      await api.put(endpoint, payload, { timeout: 30000 });
      setLinkDialog(false);
      setLinkValue("");
      setSelectedClient(null);
      fetchClients();
    } catch (error) {
      console.error(`Error updating ${linkType} link:`, error);
    }
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    client: Client
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedClientForMenu(client);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedClientForMenu(null);
  };

  const handleMenuAction = (action: string) => {
    if (!selectedClientForMenu) return;

    switch (action) {
      case "editStatus":
        setSelectedClient(selectedClientForMenu);
        setStatusUpdateDialog(true);
        break;
      case "editClient":
        navigate(`/client/${selectedClientForMenu._id}/edit`);
        break;
      case "viewClient":
        navigate(`/client/${selectedClientForMenu._id}`);
        break;
      case "profilePhotoUploadLink":
        setSelectedClient(selectedClientForMenu);
        setLinkType("profilePhoto");
        setLinkValue(selectedClientForMenu.profilePhotoUploadLink || "");
        setLinkDialog(true);
        break;
      case "idDocumentLink":
        setSelectedClient(selectedClientForMenu);
        setLinkType("idDocument");
        setLinkValue(selectedClientForMenu.idDocumentLink || "");
        setLinkDialog(true);
        break;
      case "deleteClient":
        openDeleteDialog(selectedClientForMenu);
        break;
    }

    handleMenuClose();
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: (theme) => theme.palette.grey[100],
        py: 3,
      }}
    >
      <Container maxWidth="xl">
        <Stack spacing={3}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
                <ArrowBack />
              </IconButton>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                Gerenciar Clientes
              </Typography>
            </Box>
            {user?.role === "admin" && (
              <Button
                variant="outlined"
                startIcon={<Delete />}
                onClick={() => navigate("/deleted-clients")}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Clientes Deletados
              </Button>
            )}
          </Box>

          {/* Success/Error Notifications */}
          {deleteSuccess && (
            <Alert
              severity="success"
              sx={{ borderRadius: 2 }}
              onClose={() => setDeleteSuccess(null)}
            >
              {deleteSuccess}
            </Alert>
          )}

          {deleteError && (
            <Alert
              severity="error"
              sx={{ borderRadius: 2 }}
              onClose={() => setDeleteError(null)}
            >
              {deleteError}
            </Alert>
          )}

          {/* Filter */}
          <Card sx={{ p: 3 }}>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label={uiPT.dashboard.search}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  flex: 1,
                  minWidth: 250,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: "#667eea",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#667eea",
                    },
                  },
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ mr: 1, color: "#667eea" }} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <FormControl sx={{ minWidth: 250 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status"
                  sx={{
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: "#667eea",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#667eea",
                    },
                  }}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {Object.keys(statusTextPT).map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Card>

          {/* Table */}
          <Card>
            <Box
              sx={{
                p: 3,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: "#2c3e50" }}
              >
                Lista de Clientes
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => navigate("/create-client")}
                sx={{
                  ml: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                Novo Cliente
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Código</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nome</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Modalidade</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Identidade</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Cidade</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clients.map((client) => (
                    <TableRow
                      key={client._id}
                      sx={{
                        "&:hover": {
                          background: "rgba(102, 126, 234, 0.05)",
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>
                        {client.trackingCode}
                      </TableCell>
                      <TableCell>{client.name}</TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                          >
                            {client.email}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => copyToClipboard(client.email)}
                            sx={{
                              "&:hover": {
                                background: "rgba(102, 126, 234, 0.1)",
                              },
                            }}
                          >
                            <ContentCopy sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>{client.type}</TableCell>
                      <TableCell>{client.modality}</TableCell>
                      <TableCell>
                        {client.identityType === 'passport' ? 'Passaporte' :
                         client.identityType === 'cc' ? 'CC' :
                         client.identityType === 'trc' ? 'TRC' :
                         client.identityType === 'cplp' ? 'CPLP' :
                         client.identityType}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={client.status}
                          color={getStatusColor(client.status) as any}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            "& .MuiChip-label": {
                              px: 2,
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell>{client.city}</TableCell>
                      <TableCell>
                        <IconButton
                          onClick={(event) => handleMenuOpen(event, client)}
                          sx={{
                            "&:hover": {
                              background: "rgba(102, 126, 234, 0.1)",
                            },
                          }}
                          title="Ações"
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              sx={{
                "& .MuiPaginationItem-root": {
                  borderRadius: 2,
                  fontWeight: 600,
                },
              }}
            />
          </Box>
        </Stack>
      </Container>

      {/* Status Update Dialog */}
      <Dialog
        open={statusUpdateDialog}
        onClose={() => setStatusUpdateDialog(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              minWidth: 500,
            },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: "#2c3e50" }}>
          {uiPT.dashboard.updateStatus}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2, mb: 2 }}>
            <InputLabel>Novo Status</InputLabel>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              label="Novo Status"
              sx={{
                borderRadius: 2,
                "&:hover fieldset": {
                  borderColor: "#667eea",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#667eea",
                },
              }}
            >
              {Object.keys(statusTextPT).map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {(newStatus === "VERIFICAÇÃO ENVIADA" ||
            newStatus === "VERIFICAÇÃO REJEITADA") && (
            <TextField
              label={uiPT.dashboard.verificationLink}
              value={verificationLink}
              onChange={(e) => setVerificationLink(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              slotProps={{
                input: {
                  sx: {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: "#667eea",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#667eea",
                    },
                  },
                },
              }}
            />
          )}

          {[
            "REJEITADO PELA EQUIPE",
            "DOCUMENTOS REJEITADOS",
            "DOCUMENTOS EXPIRADOS",
          ].includes(newStatus) && (
            <TextField
              label={uiPT.dashboard.rejectionReason}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              fullWidth
              multiline
              rows={3}
              sx={{ mb: 2 }}
              slotProps={{
                input: {
                  sx: {
                    borderRadius: 2,
                    "&:hover fieldset": {
                      borderColor: "#667eea",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "#667eea",
                    },
                  },
                },
              }}
            />
          )}

          {[
            "REJEITADO PELA EQUIPE",
            "DOCUMENTOS REJEITADOS",
            "DOCUMENTOS EXPIRADOS",
          ].includes(newStatus) && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Documentos Rejeitados:
              </Typography>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rejectedDocuments.selfie || false}
                      onChange={(e) =>
                        setRejectedDocuments((prev) => ({
                          ...prev,
                          selfie: e.target.checked,
                        }))
                      }
                    />
                  }
                  label={uiPT.dashboard.selfie}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rejectedDocuments.modalityPhoto || false}
                      onChange={(e) =>
                        setRejectedDocuments((prev) => ({
                          ...prev,
                          modalityPhoto: e.target.checked,
                        }))
                      }
                    />
                  }
                  label={uiPT.dashboard.modalityPhoto}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rejectedDocuments.driverLicense || false}
                      onChange={(e) =>
                        setRejectedDocuments((prev) => ({
                          ...prev,
                          driverLicense: e.target.checked,
                        }))
                      }
                    />
                  }
                  label={uiPT.dashboard.driverLicense}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rejectedDocuments.idCardFront || false}
                      onChange={(e) =>
                        setRejectedDocuments((prev) => ({
                          ...prev,
                          idCardFront: e.target.checked,
                        }))
                      }
                    />
                  }
                  label={uiPT.dashboard.idCardFront}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rejectedDocuments.idCardBack || false}
                      onChange={(e) =>
                        setRejectedDocuments((prev) => ({
                          ...prev,
                          idCardBack: e.target.checked,
                        }))
                      }
                    />
                  }
                  label={uiPT.dashboard.idCardBack}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rejectedDocuments.idCardPdf || false}
                      onChange={(e) =>
                        setRejectedDocuments((prev) => ({
                          ...prev,
                          idCardPdf: e.target.checked,
                        }))
                      }
                    />
                  }
                  label={uiPT.dashboard.idCardPdf}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rejectedDocuments.caeDocument || false}
                      onChange={(e) =>
                        setRejectedDocuments((prev) => ({
                          ...prev,
                          caeDocument: e.target.checked,
                        }))
                      }
                    />
                  }
                  label={uiPT.dashboard.caeDocument}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={rejectedDocuments.taxClearanceDocument || false}
                      onChange={(e) =>
                        setRejectedDocuments((prev) => ({
                          ...prev,
                          taxClearanceDocument: e.target.checked,
                        }))
                      }
                    />
                  }
                  label={uiPT.dashboard.taxClearanceDocument}
                />
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setStatusUpdateDialog(false)}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {uiPT.dashboard.cancel}
          </Button>
          <Button
            onClick={handleStatusUpdate}
            variant="contained"
            disabled={
              !newStatus ||
              ([
                "REJEITADO PELA EQUIPE",
                "DOCUMENTOS REJEITADOS",
                "DOCUMENTOS EXPIRADOS",
              ].includes(newStatus) &&
                !rejectionReason) ||
              ((newStatus === "VERIFICAÇÃO ENVIADA" ||
                newStatus === "VERIFICAÇÃO REJEITADA") &&
                !verificationLink) ||
              ([
                "REJEITADO PELA EQUIPE",
                "DOCUMENTOS REJEITADOS",
                "DOCUMENTOS EXPIRADOS",
              ].includes(newStatus) &&
                Object.keys(rejectedDocuments).filter(
                  (key) =>
                    rejectedDocuments[key as keyof typeof rejectedDocuments]
                ).length === 0)
            }
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {uiPT.dashboard.save}
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Preview Modal */}
      <Modal open={Boolean(filePreview)} onClose={() => setFilePreview(null)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            maxWidth: 600,
            maxHeight: 600,
            borderRadius: 3,
          }}
        >
          {filePreview &&
            (filePreview.type === "image" ? (
              <img
                src={filePreview.url}
                alt="Preview"
                style={{ maxWidth: "100%", maxHeight: "100%" }}
                onError={(e) => {
                  console.error("Failed to load image:", filePreview.url);
                  // Try fallback URL if available
                  if (filePreview.fallbackUrl) {
                    console.log(
                      "Trying fallback URL:",
                      filePreview.fallbackUrl
                    );
                    e.currentTarget.src = filePreview.fallbackUrl;
                  }
                }}
              />
            ) : (
              <iframe
                src={filePreview.url}
                width="100%"
                height="500"
                onError={(e) => {
                  console.error("Failed to load PDF:", filePreview.url);
                }}
              />
            ))}
          {filePreview && (
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {filePreview.url}
              </Typography>
              <Box
                sx={{
                  mt: 1,
                  display: "flex",
                  gap: 1,
                  justifyContent: "center",
                }}
              >
                <Button
                  onClick={() => window.open(filePreview.url, "_blank")}
                  size="small"
                >
                  Abrir em nova aba
                </Button>
                {filePreview.fallbackUrl && (
                  <Button
                    onClick={() =>
                      window.open(filePreview.fallbackUrl, "_blank")
                    }
                    size="small"
                    variant="outlined"
                  >
                    Tentar URL alternativa
                  </Button>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              minWidth: 400,
            },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: "#d32f2f" }}>
          {uiPT.dashboard.deleteConfirmTitle}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {uiPT.dashboard.deleteConfirmMessage}{" "}
            <strong>{clientToDelete?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Código: {clientToDelete?.trackingCode}
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {uiPT.dashboard.deleteWarning}
          </Alert>
          <TextField
            fullWidth
            label="Motivo da Exclusão"
            value={deletionReason}
            onChange={(e) => setDeletionReason(e.target.value)}
            placeholder="Digite o motivo da exclusão do cliente..."
            multiline
            rows={3}
            required
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog(false)}
            disabled={deleteLoading}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {uiPT.dashboard.cancel}
          </Button>
          <Button
            onClick={handleDeleteClient}
            color="error"
            variant="contained"
            disabled={deleteLoading || !deletionReason.trim()}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {deleteLoading ? uiPT.dashboard.deleting : uiPT.dashboard.delete}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Unified Link Dialog */}
      <Dialog
        open={linkDialog}
        onClose={() => setLinkDialog(false)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 3,
              minWidth: 500,
            },
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: "#2c3e50" }}>
          {linkType === "profilePhoto"
            ? "Link de Upload de Foto de Perfil"
            : "Link de Documento de Identidade"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Cliente: <strong>{selectedClient?.name}</strong>
          </Typography>
          <TextField
            fullWidth
            label={
              linkType === "profilePhoto"
                ? "URL do Link de Upload"
                : "URL do Link de Documento"
            }
            value={linkValue}
            onChange={(e) => setLinkValue(e.target.value)}
            placeholder={
              linkType === "profilePhoto"
                ? "https://example.com/upload"
                : "https://example.com/id-document"
            }
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setLinkDialog(false)}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleLinkUpdate}
            variant="contained"
            disabled={!linkValue.trim()}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              minWidth: 200,
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
            },
          },
        }}
      >
        <MenuItem onClick={() => handleMenuAction("editStatus")}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Editar Status</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction("editClient")}>
          <ListItemIcon>
            <PersonOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Editar Cliente</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction("viewClient")}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>Visualizar Cliente</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction("profilePhotoUploadLink")}>
          <ListItemIcon>
            <PhotoCamera fontSize="small" />
          </ListItemIcon>
          <ListItemText>Link de Foto de Perfil</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuAction("idDocumentLink")}>
          <ListItemIcon>
            <Description fontSize="small" />
          </ListItemIcon>
          <ListItemText>Link de Documento</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => handleMenuAction("deleteClient")}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <Delete fontSize="small" sx={{ color: "error.main" }} />
          </ListItemIcon>
          <ListItemText>Deletar Cliente</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
}
